if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        /** @type {HTMLElement} */
        this.modalContent = /** @type {HTMLElement} */ (this.querySelector('[id^="QuickAddInfo-"]'));
        /** @type {HTMLElement|null} */
        this.productElement = null;
      }

      /**
       * @param {boolean} [preventFocus=false]
       */
      hide(preventFocus = false) {
        /** @type {HTMLElement|null} */
        const cartNotification = /** @type {HTMLElement|null} */ (document.querySelector('cart-notification') || document.querySelector('cart-drawer'));
        if (cartNotification) {/** @type {any} */ (cartNotification).setActiveElement(this.openedBy);}
        this.modalContent.innerHTML = '';

        if (preventFocus) {this.openedBy = null;}
        super.hide();
      }

      /**
       * @param {HTMLElement} opener
       */
      show(opener) {
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        /** @type {HTMLElement} */ (opener.querySelector('.loading-overlay__spinner')).classList.remove('hidden');

        fetch(/** @type {string} */ (opener.getAttribute('data-product-url')))
          .then(/** @param {Response} response */ (response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then(/** @param {string} responseText */ (responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = /** @type {HTMLElement|null} */ (responseHTML.querySelector('section[id^="MainProduct-"]'));

            if (!this.productElement) {
              throw new Error('Product content not found');
            }

            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {window.ProductModel.loadShopifyXR();}

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            super.show(opener);
          })
          .catch(/** @param {Error} error */ (error) => {
            console.error('Quick add failed:', error);
            // Show user-friendly error message using safe DOM methods
            const errorDiv = document.createElement('div');
            errorDiv.className = 'quick-add-error';
            errorDiv.style.cssText = 'padding: 2rem; text-align: center;';
            const errorText = document.createElement('p');
            errorText.textContent = 'Sorry, we couldn\'t load this product. Please try again or view the product page directly.';
            errorDiv.appendChild(errorText);
            this.modalContent.replaceChildren(errorDiv);
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            /** @type {HTMLElement} */ (opener.querySelector('.loading-overlay__spinner')).classList.add('hidden');
          });
      }

      /**
       * @param {HTMLElement} element
       * @param {string} html
       */
      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach(/** @param {HTMLScriptElement} oldScriptTag */ (oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach(/** @param {Attr} attribute */ (attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          /** @type {Node} */ (oldScriptTag.parentNode).replaceChild(newScriptTag, oldScriptTag);
        });
      }

      /**
       * @returns {void}
       */
      preventVariantURLSwitching() {
        /** @type {HTMLElement|null} */
        const variantPicker = /** @type {HTMLElement|null} */ (this.modalContent.querySelector('variant-radios,variant-selects'));
        if (!variantPicker) {return;}

        variantPicker.setAttribute('data-update-url', 'false');
      }

      /**
       * @returns {void}
       */
      removeDOMElements() {
        if (!this.productElement) {return;}
        /** @type {HTMLElement|null} */
        const pickupAvailability = /** @type {HTMLElement|null} */ (this.productElement.querySelector('pickup-availability'));
        if (pickupAvailability) {pickupAvailability.remove();}

        /** @type {HTMLElement|null} */
        const productModal = /** @type {HTMLElement|null} */ (this.productElement.querySelector('product-modal'));
        if (productModal) {productModal.remove();}

        /** @type {NodeListOf<HTMLElement>} */
        const modalDialog = /** @type {NodeListOf<HTMLElement>} */ (this.productElement.querySelectorAll('modal-dialog'));
        if (modalDialog) {modalDialog.forEach(/** @param {HTMLElement} modal */ (modal) => modal.remove());}
      }

      /**
       * @returns {void}
       */
      preventDuplicatedIDs() {
        if (!this.productElement) {return;}
        const sectionId = this.productElement.dataset.section;
        if (!sectionId) {return;}
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          /** @type {HTMLElement} */ (element).dataset.originalSection = sectionId;
        });
      }

      /**
       * @returns {void}
       */
      removeGalleryListSemantic() {
        /** @type {HTMLElement|null} */
        const galleryList = /** @type {HTMLElement|null} */ (this.modalContent.querySelector('[id^="Slider-Gallery"]'));
        if (!galleryList) {return;}

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach(/** @param {Element} li */ (li) => li.setAttribute('role', 'presentation'));
      }

      /**
       * @returns {void}
       */
      updateImageSizes() {
        /** @type {HTMLElement|null} */
        const product = /** @type {HTMLElement|null} */ (this.modalContent.querySelector('.product'));
        if (!product) {return;}
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) {return;}

        /** @type {NodeListOf<HTMLImageElement>} */
        const mediaImages = /** @type {NodeListOf<HTMLImageElement>} */ (product.querySelectorAll('.product__media img'));
        if (!mediaImages.length) {return;}

        /** @type {string} */
        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach(/** @param {HTMLImageElement} img */ (img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
